import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  GetAllPoderCalorificoNominationAction,
  GetPoderCalorificoNominationOptionsAction,
  UploadPoderCalorificoNominationAction,
  ConfirmPoderCalorificoNominationAction,
  DownloadPoderCalorificoNominationTemplateAction,
  ClearPoderCalorificoNominationDataAction,
} from './poder-calorifico-nomination.actions';
import {
  IPoderCalorificoNomination,
  IPoderCalorificoNominationOptions,
  IPoderCalorificoResponse,
} from '../../core/interfaces/poder-calorifico-nomination.interface';
import { PoderCalorificoNominationService } from '../../core/services/poder-calorifico-nomination/poder-calorifico-nomination.service';
import { MatDialog } from '@angular/material/dialog';
import { EnovaAlertsComponent } from '../../core/utils/enova-alerts/enova-alerts.component';

export interface PoderCalorificoNominationStateModel {
  poderCalorificoData: IPoderCalorificoResponse;
  options: IPoderCalorificoNominationOptions | null;
  loading: boolean;
}

@State<PoderCalorificoNominationStateModel>({
  name: 'poderCalorificoNomination',
  defaults: {
    poderCalorificoData: [],
    options: null,
    loading: false,
  },
})
@Injectable()
export class PoderCalorificoNominationState {
  @Selector()
  static GetAllData(
    state: PoderCalorificoNominationStateModel
  ): IPoderCalorificoResponse {
    return state?.poderCalorificoData ? state?.poderCalorificoData : [];
  }

  @Selector()
  static GetOptions(
    state: PoderCalorificoNominationStateModel
  ): IPoderCalorificoNominationOptions | null {
    return state?.options || null;
  }

  @Selector()
  static IsLoading(state: PoderCalorificoNominationStateModel): boolean {
    return state?.loading || false;
  }

  constructor(
    private poderCalorificoService: PoderCalorificoNominationService,
    private dialog: MatDialog
  ) {}

  @Action(GetAllPoderCalorificoNominationAction)
  async GetAllPoderCalorificoNominationAction(
    ctx: StateContext<PoderCalorificoNominationStateModel>,
    { query }: GetAllPoderCalorificoNominationAction
  ) {
    ctx.patchState({ loading: true });
    try {
      const resp = await this.poderCalorificoService.getAllPoderCalorifico(
        query
      );
      ctx.patchState({
        poderCalorificoData: resp || [],
        loading: false,
      });
    } catch (error) {
      console.error('Error al obtener datos de poder calorífico:', error);
      ctx.patchState({
        poderCalorificoData: [],
        loading: false,
      });
    }
  }

  @Action(GetPoderCalorificoNominationOptionsAction)
  async GetPoderCalorificoNominationOptionsAction(
    ctx: StateContext<PoderCalorificoNominationStateModel>
  ) {
    try {
      const options =
        await this.poderCalorificoService.getPoderCalorificoOptions();
      ctx.patchState({
        options: options,
      });
    } catch (error) {
      console.error('Error al obtener opciones:', error);
      ctx.patchState({
        options: null,
      });
    }
  }

  @Action(UploadPoderCalorificoNominationAction)
  async UploadPoderCalorificoNominationAction(
    ctx: StateContext<PoderCalorificoNominationStateModel>,
    { payload }: UploadPoderCalorificoNominationAction
  ) {
    ctx.patchState({ loading: true });
    try {
      const result =
        await this.poderCalorificoService.uploadPoderCalorifico(payload);

      if (result.success) {
        // Recargar datos después de subir
        ctx.dispatch(new GetAllPoderCalorificoNominationAction());

        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'success',
            text: result.message || 'Archivo subido correctamente',
            hiddeCancelBtn: true,
          },
        });
      } else {
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            text: result.message || 'Error al subir el archivo',
            hiddeCancelBtn: true,
          },
        });
      }
      ctx.patchState({ loading: false });
    } catch (error: any) {
      ctx.patchState({ loading: false });
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'error',
          text: error.message || 'Error al subir el archivo',
          hiddeCancelBtn: true,
        },
      });
    }
  }

  @Action(ConfirmPoderCalorificoNominationAction)
  async ConfirmPoderCalorificoNominationAction(
    ctx: StateContext<PoderCalorificoNominationStateModel>,
    { payload }: ConfirmPoderCalorificoNominationAction
  ) {
    ctx.patchState({ loading: true });
    try {
      const result = await this.poderCalorificoService.confirmMultipleValues(payload);

      if (result.success) {
        // Recargar datos después de confirmar
        ctx.dispatch(new GetAllPoderCalorificoNominationAction());

        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'success',
            text: result.message || 'Valores confirmados correctamente',
            hiddeCancelBtn: true,
          },
        });
      } else {
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            text: result.message || 'Error al confirmar valores',
            hiddeCancelBtn: true,
          },
        });
      }
      ctx.patchState({ loading: false });
    } catch (error: any) {
      ctx.patchState({ loading: false });
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'error',
          text: error.message || 'Error al confirmar valores',
          hiddeCancelBtn: true,
        },
      });
    }
  }

  @Action(DownloadPoderCalorificoNominationTemplateAction)
  async DownloadPoderCalorificoNominationTemplateAction(
    ctx: StateContext<PoderCalorificoNominationStateModel>
  ) {
    try {
      const blob = await this.poderCalorificoService.downloadTemplate();
      
      // Crear URL del blob y descargar
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template-poder-calorifico.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'error',
          text: error.message || 'Error al descargar el template',
          hiddeCancelBtn: true,
        },
      });
    }
  }

  @Action(ClearPoderCalorificoNominationDataAction)
  ClearPoderCalorificoNominationDataAction(
    ctx: StateContext<PoderCalorificoNominationStateModel>
  ) {
    this.poderCalorificoService.clearLocalData();
    ctx.patchState({
      poderCalorificoData: [],
      options: null,
    });
  }
}
